import { ApiError } from './client';

// Streaming requests are never retried here. The conversation's durable runs
// provide recovery after navigation or a lost connection.
export async function readBusinessStream(response, { workspaceId, conversationId, onDelta = () => {},
  isRequestCurrent = () => true, signal, timeoutMs = 150000 } = {}) {
  const invalid = () => new ApiError('This request is no longer active.', 0, { code: 'REQUEST_INVALIDATED', silent: true });
  if (!isRequestCurrent() || signal?.aborted) { await response.body?.cancel(); throw invalid(); }
  if (!response.body?.getReader || !/text\/event-stream/i.test(response.headers.get('content-type') || '')) {
    await response.body?.cancel(); throw new ApiError('The reply could not be read. Check its status before sending again.', 0);
  }
  const reader = response.body.getReader(), decoder = new TextDecoder();
  let buffer = '', bytes = 0, result = null, stopped = false;
  const cancel = () => { stopped = true; void reader.cancel().catch(() => {}); };
  signal?.addEventListener('abort', cancel, { once: true });
  const timer = setTimeout(cancel, timeoutMs);
  try {
    while (!result) {
      const chunk = await reader.read();
      if (!isRequestCurrent() || signal?.aborted) throw invalid();
      if (stopped || chunk.done) break;
      bytes += chunk.value.byteLength;
      if (bytes > 10 * 1024 * 1024) throw new ApiError('The reply exceeded the supported size.', 0);
      buffer = (buffer + decoder.decode(chunk.value, { stream: true })).replace(/\r\n/g, '\n');
      let boundary;
      while ((boundary = buffer.indexOf('\n\n')) >= 0) {
        const frame = buffer.slice(0, boundary); buffer = buffer.slice(boundary + 2);
        const lines = frame.split('\n'), name = lines.find(line => line.startsWith('event:'))?.slice(6).trim();
        const data = lines.filter(line => line.startsWith('data:')).map(line => line.slice(5).trim()).join('\n');
        if (!data) continue;
        const payload = JSON.parse(data);
        if (!isRequestCurrent() || signal?.aborted) throw invalid();
        if (name === 'error') throw new ApiError(payload.message || 'The reply was interrupted.', 0, payload);
        if (name === 'delta') {
          if (typeof payload.content !== 'string') throw new ApiError('The reply could not be read.', 0);
          onDelta(payload.content);
        }
        if (name === 'complete') {
          if (payload.workspaceId !== workspaceId || payload.conversationId !== conversationId || typeof payload.id !== 'string') {
            throw new ApiError('The reply belongs to another chat.', 0);
          }
          result = payload; break;
        }
      }
    }
    if (!result) throw new ApiError('The connection ended before the reply was confirmed. Check its status before sending again.', 0);
    return result;
  } finally {
    clearTimeout(timer); signal?.removeEventListener('abort', cancel);
    await reader.cancel().catch(() => {}); reader.releaseLock();
  }
}
